import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Image, Plus, Edit, Trash2, Eye, EyeOff, ArrowUp, ArrowDown } from 'lucide-react'
import { getImageUrl } from '../utils/apiUrl'
import apiService from '../services/api'
import toast from 'react-hot-toast'
import AdminLayout from '../components/AdminLayout'

const AdminBanners = () => {
  const [banners, setBanners] = useState([])
  const [isLoadingBanners, setIsLoadingBanners] = useState(false)
  const [editingBanner, setEditingBanner] = useState(null)
  const [showBannerForm, setShowBannerForm] = useState(false)
  const [isSavingBanner, setIsSavingBanner] = useState(false)
  
  const navigate = useNavigate()

  // Charger les bannières au montage du composant
  useEffect(() => {
    fetchBanners()
  }, [])

  const fetchBanners = async () => {
    setIsLoadingBanners(true)
    try {
      const response = await apiService.get('/banners')
      setBanners(response.data)
    } catch (error) {
      console.error('Erreur chargement bannières:', error)
      toast.error('Erreur lors du chargement des bannières')
    } finally {
      setIsLoadingBanners(false)
    }
  }

  const handleSaveBanner = async (bannerData) => {
    setIsSavingBanner(true)
    try {
      if (editingBanner) {
        await apiService.put(`/banners/${editingBanner.id}`, bannerData)
        toast.success('Bannière mise à jour avec succès')
      } else {
        await apiService.post('/banners', bannerData)
        toast.success('Bannière créée avec succès')
      }
      await fetchBanners()
      setShowBannerForm(false)
      setEditingBanner(null)
    } catch (error) {
      console.error('Erreur sauvegarde bannière:', error)
      toast.error('Erreur lors de la sauvegarde de la bannière')
    } finally {
      setIsSavingBanner(false)
    }
  }

  const handleDeleteBanner = async (bannerId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette bannière ?')) {
      return
    }
    
    try {
      await apiService.delete(`/banners/${bannerId}`)
      toast.success('Bannière supprimée avec succès')
      await fetchBanners()
    } catch (error) {
      console.error('Erreur suppression bannière:', error)
      toast.error('Erreur lors de la suppression de la bannière')
    }
  }

  const handleTogglePublished = async (banner) => {
    try {
      await apiService.put(`/banners/${banner.id}`, {
        ...banner,
        is_published: !banner.is_published
      })
      toast.success(`Bannière ${!banner.is_published ? 'publiée' : 'dépubliée'} avec succès`)
      await fetchBanners()
    } catch (error) {
      console.error('Erreur toggle publication:', error)
      toast.error('Erreur lors de la mise à jour de la bannière')
    }
  }

  const handleMoveBanner = async (bannerId, direction) => {
    const banner = banners.find(b => b.id === bannerId)
    if (!banner) return

    const currentIndex = banners.findIndex(b => b.id === bannerId)
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1

    if (newIndex < 0 || newIndex >= banners.length) return

    const targetBanner = banners[newIndex]
    
    try {
      // Échanger les ordres
      await apiService.put(`/banners/${bannerId}`, {
        ...banner,
        sort_order: targetBanner.sort_order
      })
      await apiService.put(`/banners/${targetBanner.id}`, {
        ...targetBanner,
        sort_order: banner.sort_order
      })
      
      toast.success('Ordre des bannières mis à jour')
      await fetchBanners()
    } catch (error) {
      console.error('Erreur changement ordre:', error)
      toast.error('Erreur lors du changement d\'ordre')
    }
  }

  return (
    <AdminLayout title="Bannières" subtitle="Gestion des bannières de la page d'accueil">
      <div className="space-y-6">
        {/* En-tête */}
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Gestion des bannières</h3>
            <p className="text-sm text-gray-600">Gérez les bannières affichées sur la page d'accueil</p>
          </div>
          <button
            onClick={() => {
              setEditingBanner(null)
              setShowBannerForm(true)
            }}
            className="inline-flex items-center px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nouvelle bannière
          </button>
        </div>

        {/* Liste des bannières */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6">
            {isLoadingBanners ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto"></div>
                <p className="text-gray-600 mt-2">Chargement des bannières...</p>
              </div>
            ) : (
              <div className="space-y-4">
                {banners.map((banner, index) => (
                  <div key={banner.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center space-x-4 flex-1">
                      {/* Miniature de l'image */}
                      <div className="flex-shrink-0">
                        {banner.image_path ? (
                          <img
                            src={getImageUrl(banner.image_path)}
                            alt={`Bannière ${banner.title}`}
                            className="w-20 h-12 object-cover rounded-lg shadow-sm border border-gray-200"
                            onError={(e) => {
                              e.target.style.display = 'none'
                              e.target.nextSibling.style.display = 'flex'
                            }}
                          />
                        ) : null}
                        <div 
                          className="w-20 h-12 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center"
                          style={{ display: banner.image_path ? 'none' : 'flex' }}
                        >
                          <Image className="h-6 w-6 text-gray-400" />
                        </div>
                      </div>
                      
                      {/* Informations de la bannière */}
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <h4 className="font-medium text-gray-900">{banner.title}</h4>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            banner.is_published 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {banner.is_published ? 'Publiée' : 'Brouillon'}
                          </span>
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Ordre: {banner.sort_order}
                          </span>
                        </div>
                        {banner.subtitle && (
                          <p className="text-sm text-gray-600 mt-1">{banner.subtitle}</p>
                        )}
                        {banner.button_text && (
                          <p className="text-xs text-gray-500 mt-1">
                            Bouton: "{banner.button_text}" → {banner.button_link}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      {/* Boutons de déplacement */}
                      <button
                        onClick={() => handleMoveBanner(banner.id, 'up')}
                        disabled={index === 0}
                        className={`p-2 rounded transition-colors ${
                          index === 0 
                            ? 'text-gray-300 cursor-not-allowed' 
                            : 'text-gray-600 hover:text-orange-600'
                        }`}
                        title="Déplacer vers le haut"
                      >
                        <ArrowUp className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleMoveBanner(banner.id, 'down')}
                        disabled={index === banners.length - 1}
                        className={`p-2 rounded transition-colors ${
                          index === banners.length - 1 
                            ? 'text-gray-300 cursor-not-allowed' 
                            : 'text-gray-600 hover:text-orange-600'
                        }`}
                        title="Déplacer vers le bas"
                      >
                        <ArrowDown className="h-4 w-4" />
                      </button>
                      
                      {/* Bouton publication */}
                      <button
                        onClick={() => handleTogglePublished(banner)}
                        className={`p-2 rounded transition-colors ${
                          banner.is_published 
                            ? 'text-green-600 hover:text-green-700' 
                            : 'text-gray-600 hover:text-gray-700'
                        }`}
                        title={banner.is_published ? 'Dépublier' : 'Publier'}
                      >
                        {banner.is_published ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                      </button>
                      
                      {/* Bouton modifier */}
                      <button
                        onClick={() => {
                          setEditingBanner(banner)
                          setShowBannerForm(true)
                        }}
                        className="p-2 text-gray-600 hover:text-orange-600 transition-colors"
                        title="Modifier"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      
                      {/* Bouton supprimer */}
                      <button
                        onClick={() => handleDeleteBanner(banner.id)}
                        className="p-2 text-gray-600 hover:text-red-600 transition-colors"
                        title="Supprimer"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
                
                {banners.length === 0 && (
                  <div className="text-center py-8">
                    <Image className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h4 className="text-lg font-medium text-gray-900 mb-2">Aucune bannière</h4>
                    <p className="text-gray-600 mb-4">Commencez par ajouter votre première bannière.</p>
                    <button
                      onClick={() => {
                        setEditingBanner(null)
                        setShowBannerForm(true)
                      }}
                      className="inline-flex items-center px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Ajouter une bannière
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Formulaire de bannière */}
        {showBannerForm && (
          <BannerForm
            banner={editingBanner}
            onSave={handleSaveBanner}
            onCancel={() => {
              setShowBannerForm(false)
              setEditingBanner(null)
            }}
            isSaving={isSavingBanner}
          />
        )}
      </div>
    </AdminLayout>
  )
}

// Composant formulaire de bannière
const BannerForm = ({ banner, onSave, onCancel, isSaving }) => {
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    button_text: '',
    button_link: '',
    image_path: '',
    is_published: false,
    sort_order: 0
  })

  const [selectedFile, setSelectedFile] = useState(null)
  const [availableImages, setAvailableImages] = useState([])

  useEffect(() => {
    if (banner) {
      setFormData({
        title: banner.title || '',
        subtitle: banner.subtitle || '',
        button_text: banner.button_text || '',
        button_link: banner.button_link || '',
        image_path: banner.image_path || '',
        is_published: banner.is_published || false,
        sort_order: banner.sort_order || 0
      })
    }
  }, [banner])

  // Charger les images disponibles
  useEffect(() => {
    const fetchImages = async () => {
      try {
        const response = await apiService.get('/upload/images?folder=banners')
        setAvailableImages(response.data.images || [])
      } catch (error) {
        console.error('Erreur chargement images:', error)
      }
    }
    fetchImages()
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Si un fichier est sélectionné, l'uploader d'abord
    if (selectedFile) {
      try {
        // Convertir l'image en base64
        const reader = new FileReader()
        reader.onloadend = async () => {
          try {
            const response = await apiService.post('/upload/image', {
              image: reader.result,
              filename: selectedFile.name,
              folder: 'banners'
            })

            if (response.data && response.data.path) {
              // Mettre à jour le formData avec le chemin de l'image
              const updatedFormData = {
                ...formData,
                image_path: response.data.path
              }
              onSave(updatedFormData)
            }
          } catch (error) {
            console.error('Erreur upload image:', error)
            toast.error('Erreur lors de l\'upload de l\'image')
          }
        }
        reader.readAsDataURL(selectedFile)
      } catch (error) {
        console.error('Erreur lecture fichier:', error)
        toast.error('Erreur lors de la lecture du fichier')
      }
    } else {
      // Pas de fichier sélectionné, sauvegarder directement
      onSave(formData)
    }
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const handleFileSelect = (e) => {
    const file = e.target.files[0]
    if (!file) return

    // Validation de la taille (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Le fichier est trop volumineux (max 5MB)')
      return
    }

    // Validation du type
    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez sélectionner un fichier image')
      return
    }

    setSelectedFile(file)
    toast.info('Image sélectionnée : ' + file.name)
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h4 className="text-lg font-semibold text-gray-900 mb-4">
        {banner ? 'Modifier la bannière' : 'Nouvelle bannière'}
      </h4>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Titre */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Titre *
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              placeholder="Ex: Bienvenue sur FlightCase"
            />
          </div>

          {/* Sous-titre */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sous-titre
            </label>
            <textarea
              name="subtitle"
              value={formData.subtitle}
              onChange={handleChange}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              placeholder="Ex: Votre partenaire pour la location de matériel cinématographique"
            />
          </div>

          {/* Texte du bouton */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Texte du bouton
            </label>
            <input
              type="text"
              name="button_text"
              value={formData.button_text}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              placeholder="Ex: Découvrir nos produits"
            />
          </div>

          {/* Lien du bouton */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Lien du bouton
            </label>
            <input
              type="text"
              name="button_link"
              value={formData.button_link}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              placeholder="Ex: /catalogue"
            />
          </div>

          {/* Image */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Image *
            </label>
            
            {/* Upload d'image */}
            <div className="mb-3">
              <input
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100"
              />
              <p className="text-xs text-gray-500 mt-1">
                {selectedFile ? `✅ Fichier sélectionné : ${selectedFile.name}` : 'Formats acceptés : JPG, PNG, WebP (max 5MB)'}
              </p>
              {selectedFile && (
                <p className="text-xs text-orange-600 mt-1">
                  💡 L'image sera uploadée lors de la sauvegarde de la bannière
                </p>
              )}
            </div>

            <div className="mb-3 text-center text-sm text-gray-500">
              ou
            </div>

            {/* Sélecteur d'image */}
            <div className="mb-3">
              <select
                name="image_path"
                value={formData.image_path}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                <option value="">Sélectionner une image existante</option>
                {availableImages.map((img) => (
                  <option key={img} value={img}>
                    {img.split('/').pop()}
                  </option>
                ))}
              </select>
            </div>

            {/* Aperçu de l'image */}
            {formData.image_path && (
              <div className="mb-3 text-center">
                <div className="inline-block bg-gray-50 p-4 rounded-xl">
                  <img
                    src={getImageUrl(formData.image_path)}
                    alt="Aperçu bannière"
                    className="max-w-[300px] max-h-[150px] w-auto h-auto object-contain rounded-lg shadow-lg"
                    onError={(e) => {
                      e.target.style.display = 'none'
                    }}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {formData.image_path.split('/').pop()}
                </p>
              </div>
            )}
          </div>

          {/* Ordre d'affichage */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Ordre d'affichage
            </label>
            <input
              type="number"
              name="sort_order"
              value={formData.sort_order}
              onChange={handleChange}
              min="0"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">
              Plus le nombre est petit, plus la bannière apparaîtra en premier
            </p>
          </div>

          {/* Publication */}
          <div className="flex items-center">
            <input
              type="checkbox"
              name="is_published"
              checked={formData.is_published}
              onChange={handleChange}
              className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
            />
            <label className="ml-2 block text-sm text-gray-700">
              Publier sur le site
            </label>
          </div>
        </div>

        {/* Boutons */}
        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={isSaving}
            className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? 'Sauvegarde...' : (banner ? 'Mettre à jour' : 'Créer')}
          </button>
        </div>
      </form>
    </div>
  )
}

export default AdminBanners
